import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function Top5VancouverWildlifeHotspots() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "top-5-vancouver-wildlife-hotspots";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Stanley Park",
    "section-2": "Pacific Spirit Regional Park",
    "section-3": "Grouse Mountain Refuge",
    "section-4": "Capilano River Regional Park",
    "section-5": "Reifel Migratory Bird Sanctuary",
    "section-6": "Maplewood Flats",
    "section-7": "Burnaby Lake",
    "section-8": "Boundary Bay",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  // (Using placeholders that reflect real references in the conversation)
  const referencesData = [
    {
      url: "https://stanleyparkecology.ca",
      label: "Stanley Park Ecology Society",
    },
    {
      url: "https://www.metrovancouver.org/services/parks",
      label: "Metro Vancouver Regional Parks",
    },
    {
      url: "https://www.reifelbirdsanctuary.com",
      label: "George C. Reifel Migratory Bird Sanctuary",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // section ID used in the article. Coordinates must match exactly.
  const locations = [
    {
      id: "section-1",
      lat: 49.30426,
      lng: -123.14379,
      popupText: "Stanley Park",
    },
    {
      id: "section-2",
      lat: 49.25410,
      lng: -123.22430,
      popupText: "Pacific Spirit Regional Park",
    },
    {
      id: "section-3",
      lat: 49.37956,
      lng: -123.08180,
      popupText: "Grouse Mountain Refuge for Endangered Wildlife",
    },
    {
      id: "section-4",
      lat: 49.35650,
      lng: -123.11017,
      popupText: "Capilano River Regional Park & Salmon Hatchery",
    },
    {
      id: "section-5",
      lat: 49.09750,
      lng: -123.16611,
      popupText: "George C. Reifel Migratory Bird Sanctuary",
    },
    {
      id: "section-6",
      lat: 49.30611,
      lng: -123.00500,
      popupText: "Maplewood Flats Conservation Area",
    },
    {
      id: "section-7",
      lat: 49.24243,
      lng: -122.94607,
      popupText: "Burnaby Lake Regional Park",
    },
    {
      id: "section-8",
      lat: 49.01806,
      lng: -123.04278,
      popupText: "Boundary Bay Regional Park",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Top 5 Vancouver Wildlife Hotspots: A Practical Guide</title>
        <meta
          name="description"
          content="Explore top Vancouver wildlife hotspots including Stanley Park, Pacific Spirit, Capilano River, Grouse Mountain Refuge, Reifel Migratory Bird Sanctuary, and more."
        />
        <meta
          name="keywords"
          content="vancouver wildlife, stanley park, birding, pacific spirit park, reifel sanctuary"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/top-5-vancouver-wildlife-hotspots.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Top 5 Vancouver Wildlife Hotspots: A Practical Guide"
        />
        <meta
          property="og:description"
          content="Discover Vancouver's wild side in Stanley Park, Pacific Spirit Park, Grouse Mountain Refuge, and more. Tips, accessibility info, and local regulations included."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/top-5-vancouver-wildlife-hotspots.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/top-5-vancouver-wildlife-hotspots.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Top 5 Vancouver Wildlife Hotspots: A Practical Guide"
        />
        <meta
          name="twitter:description"
          content="Plan your wildlife adventure in Vancouver with these prime locations for birdwatching, bears, salmon, and more. Family-friendly and packed with helpful info."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/top-5-vancouver-wildlife-hotspots.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="A collage of Vancouver wildlife hotspots"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* ================== INTRODUCTION ================== */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Vancouver is far more than just its urban skyline—it's also home
              to thriving pockets of wildlife. With a stunning mix of coastline,
              forest, and wetlands, the city offers ample opportunities to
              observe diverse species in their natural habitats. Whether you’re
              a dedicated birder, a family looking for a fun outdoor adventure,
              or simply curious about local ecology, Vancouver’s wild side has
              something for everyone.
            </p>
            <p>
              In this guide, we’ll explore the top wildlife hotspots—from the
              famous Stanley Park to lesser-known sanctuaries—covering when to
              visit, what to watch for, accessibility, and essential regulations
              to protect both visitors and the resident fauna.
            </p>

            {/* ================== SECTION 1: Stanley Park ================== */}
            <h2 id="section-1" ref={(el) => setSpotRef("section-1", el)}>
              Stanley Park
            </h2>
            <p>
              Stanley Park is Vancouver’s iconic, lush oasis right next to
              downtown. Covering over 400 hectares, this park houses old-growth
              temperate rainforest, freshwater lagoons, and intertidal zones
              along the seawall. Visitors can spot everything from{" "}
              <strong>bald eagles</strong>, <strong>great blue herons</strong>,
              and <strong>raccoons</strong> to river otters and even the
              occasional coyote.
            </p>
            <p style={{ scrollMarginTop: "80px" }}>
              <strong>Marker:</strong>{" "}
              <Marker
                coordY={49.30426}
                coordX={-123.14379}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – <em>Stanley Park</em> is easy to navigate with paved trails
              suitable for wheelchairs and strollers. Early mornings or late
              afternoons are best for wildlife spotting. Don’t miss Lost Lagoon
              or Beaver Lake for birdwatching. Avoid feeding animals; human food
              can harm them and disrupt natural behaviors.
            </p>

            {/* ================== SECTION 2: Pacific Spirit Regional Park ================== */}
            <h2 id="section-2" ref={(el) => setSpotRef("section-2", el)}>
              Pacific Spirit Regional Park
            </h2>
            <p>
              Located near the University of British Columbia, Pacific Spirit
              spans nearly 900 hectares of forest and shoreline. It offers a
              quieter, more immersive experience in Vancouver’s coastal
              rainforest. Trails weave through moss-laden trees, and small
              wetlands provide habitat for birds, amphibians, and small
              mammals.
            </p>
            <p style={{ scrollMarginTop: "80px" }}>
              <strong>Marker:</strong>{" "}
              <Marker
                coordY={49.25410}
                coordX={-123.22430}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – <em>Pacific Spirit Regional Park</em> is known for barred owls,
              woodpeckers, and even the occasional coyote or black-tailed deer.
              Family-friendly trails are generally flat, though unpaved. Spring
              is especially lively with nesting birds and blooming wildflowers.
            </p>

            {/* ================== SECTION 3: Grouse Mountain Refuge ================== */}
            <h2 id="section-3" ref={(el) => setSpotRef("section-3", el)}>
              Grouse Mountain Refuge for Endangered Wildlife
            </h2>
            <p>
              A short drive from downtown, Grouse Mountain is famous for its
              panoramic views—and also for its Refuge for Endangered Wildlife.
              Visitors can see two rescued <strong>grizzly bears</strong> in a
              spacious habitat and learn about local conservation efforts. Bird
              of prey demonstrations provide an up-close look at hawks, owls,
              and falcons.
            </p>
            <p style={{ scrollMarginTop: "80px" }}>
              <strong>Marker:</strong>{" "}
              <Marker
                coordY={49.37956}
                coordX={-123.08180}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – <em>Grouse Mountain</em> is accessible via gondola (ticketed),
              with easy boardwalks around the refuge. Summer months see active
              bears while winter offers hibernation insights. Guided ranger
              talks share the stories of the orphaned grizzlies, “Grinder” and
              “Coola.”
            </p>

            {/* ================== SECTION 4: Capilano River ================== */}
            <h2 id="section-4" ref={(el) => setSpotRef("section-4", el)}>
              Capilano River Regional Park & Salmon Hatchery
            </h2>
            <p>
              The Capilano River park features a dramatic canyon, dense forest,
              and the popular{" "}
              <strong>Capilano Salmon Hatchery</strong>. During salmon runs
              (autumn especially), visitors can witness these fish leaping the
              hatchery ladders, a spectacle that also attracts <strong>bald
              eagles</strong>, <strong>otters</strong>, and occasionally <strong>black bears</strong>.
            </p>
            <p style={{ scrollMarginTop: "80px" }}>
              <strong>Marker:</strong>{" "}
              <Marker
                coordY={49.35650}
                coordX={-123.11017}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – <em>Capilano River Regional Park</em> has free admission, with
              parking near the hatchery. Trails can be steep, but there are
              multiple viewpoints and some accessible routes near Cleveland Dam.
              Autumn (Sept–Nov) is peak for salmon—and for eagles eyeing a meal.
            </p>

            {/* ================== SECTION 5: Reifel Migratory Bird Sanctuary ================== */}
            <h2 id="section-5" ref={(el) => setSpotRef("section-5", el)}>
              George C. Reifel Migratory Bird Sanctuary
            </h2>
            <p>
              A must-visit for serious and casual birders alike, the Reifel
              Sanctuary in Delta hosts hundreds of thousands of migratory birds
              along the Pacific Flyway. Expect to see <strong>snow geese</strong>{" "}
              in winter, <strong>sandhill cranes</strong>, and myriad ducks
              year-round. It’s family-friendly, with flat trails and bird blinds
              for close observation.
            </p>
            <p style={{ scrollMarginTop: "80px" }}>
              <strong>Marker:</strong>{" "}
              <Marker
                coordY={49.09750}
                coordX={-123.16611}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – <em>Reifel Migratory Bird Sanctuary</em> requires a small entry
              fee and a reservation. Don’t forget binoculars or a camera.
              October to December offers spectacular views of massive flocks of
              snow geese and other waterfowl.
            </p>

            {/* ================== SECTION 6: Maplewood Flats ================== */}
            <h2 id="section-6" ref={(el) => setSpotRef("section-6", el)}>
              Maplewood Flats Conservation Area
            </h2>
            <p>
              Nestled in North Vancouver, Maplewood Flats features mudflats,
              marsh, and second-growth forest that attract over 200 bird
              species. It's quieter than Reifel but equally rewarding, with
              potential sightings of <strong>ospreys</strong>, <strong>owls</strong>, and shorebirds along the tidal flats.
            </p>
            <p style={{ scrollMarginTop: "80px" }}>
              <strong>Marker:</strong>{" "}
              <Marker
                coordY={49.30611}
                coordX={-123.00500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – <em>Maplewood Flats</em> is free and offers flat, accessible
              boardwalks. Stick to the trails; venturing onto the mudflats can
              be dangerous and disturbs vital shorebird feeding areas.
            </p>

            {/* ================== SECTION 7: Burnaby Lake ================== */}
            <h2 id="section-7" ref={(el) => setSpotRef("section-7", el)}>
              Burnaby Lake Regional Park
            </h2>
            <p>
              A freshwater haven in an urban setting, Burnaby Lake teems with{" "}
              <strong>waterfowl</strong>, <strong>bald eagles</strong>, and
              occasional <strong>otters</strong> or <strong>beavers</strong>.
              Trails surround the lake for a full loop, or visit Piper Spit for a
              short stroll and a boardwalk offering up-close looks at ducks and
              herons.
            </p>
            <p style={{ scrollMarginTop: "80px" }}>
              <strong>Marker:</strong>{" "}
              <Marker
                coordY={49.24243}
                coordX={-122.94607}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – <em>Burnaby Lake Regional Park</em> has accessible paths at Piper
              Spit. Winter is ideal for spotting overwintering waterfowl. Watch
              for osprey nests on platforms in spring.
            </p>

            {/* ================== SECTION 8: Boundary Bay ================== */}
            <h2 id="section-8" ref={(el) => setSpotRef("section-8", el)}>
              Boundary Bay Regional Park
            </h2>
            <p>
              Straddling the Canada–US border, Boundary Bay’s extensive tidal
              flats are a magnet for <strong>shorebirds</strong>, <strong>raptors</strong>, and massive flocks of <strong>snow geese</strong>. 
              In winter, you may spot dozens of <strong>bald eagles</strong> perched along driftwood and occasional sightings 
              of <strong>short-eared owls</strong> or <strong>northern harriers</strong> hunting over the marsh.
            </p>
            <p style={{ scrollMarginTop: "80px" }}>
              <strong>Marker:</strong>{" "}
              <Marker
                coordY={49.01806}
                coordX={-123.04278}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
              – <em>Boundary Bay Regional Park</em> includes Centennial Beach, a
              family-friendly area with amenities. The dyke trail stretches for
              kilometers and offers wide-open views ideal for spotting eagles
              and hawks.
            </p>

            {/* ================== LOCAL REGULATIONS ================== */}
            <h2
              id="local-regulations"
              ref={(el) => setSpotRef("local-regulations", el)}
            >
              Local Regulations
            </h2>
            <p>
              <strong>Stay on designated trails:</strong> Protect sensitive
              habitat by following posted signs and paths.
            </p>
            <p>
              <strong>No feeding wildlife:</strong> Human food disrupts natural
              diets and can make animals dangerously habituated.
            </p>
            <p>
              <strong>Leash dogs:</strong> Most parks require dogs to be leashed
              or prohibit pets entirely, especially in bird sanctuaries.
            </p>
            <p>
              <strong>Respect seasonal closures:</strong> Some areas close to
              protect nesting birds or denning animals. Check official park
              websites for updates.
            </p>
            <p>
              <strong>Keep your distance:</strong> Use binoculars or zoom lenses
              to avoid stressing wildlife, especially during nesting or feeding
              seasons.
            </p>

            {/* ================== FAQ ================== */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. When is the best time to see wildlife in Vancouver?</h3>
              <p>
                Each season offers unique opportunities. Spring brings nesting
                birds; fall is prime for salmon runs and migratory birds; winter
                sees large numbers of bald eagles and waterfowl; and summer
                offers grizzly viewing at Grouse Mountain.
              </p>

              <h3>2. Are these spots accessible by public transit?</h3>
              <p>
                Many are: Stanley Park, Grouse Mountain, Burnaby Lake, and
                Pacific Spirit Park have bus connections. Reifel Sanctuary is
                more car-dependent, and Maplewood Flats is accessible via a
                short bus ride from Phibbs Exchange.
              </p>

              <h3>3. Can I bring my dog along?</h3>
              <p>
                Some parks allow leashed dogs, but sanctuaries like Reifel and
                Maplewood Flats prohibit pets to protect wildlife. Always check
                signs and follow posted regulations.
              </p>
            </div>

            {/* ================== CONCLUSION ================== */}
            <h2 id="conclusion" ref={(el) => setSpotRef("conclusion", el)}>
              Conclusion
            </h2>
            <p>
              Vancouver’s blend of urban amenities and natural wonders makes it
              an incredible destination for wildlife enthusiasts. Whether you’re
              wandering the seawall in Stanley Park or venturing into the tidal
              flats of Boundary Bay, you’ll find countless species thriving in
              these green spaces. By following local regulations and respecting
              these habitats, you can enjoy memorable wildlife encounters while
              ensuring future generations can do the same.
            </p>
            <p>
              Grab your binoculars, pick a hotspot, and immerse yourself in the
              wild side of Vancouver.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}